/**
 * Cart Icon Manager - Handles dynamic cart icon updates
 * Integrates with CartManager to provide real-time cart count updates
 * Requirements: 1.1, 1.2, 1.3, 1.4
 */

class CartIconManager {
  constructor() {
    this.cartIcons = [];
    this.isInitialized = false;
    
    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  /**
   * Initialize cart icon manager
   */
  init() {
    if (this.isInitialized) return;
    
    this.findCartIcons();
    this.bindEvents();
    this.isInitialized = true;
    
    // Sync with current cart state if CartManager is available
    if (window.cartManager) {
      this.updateAllIcons(window.cartManager.getItemCount());
    }
  }

  /**
   * Find all cart icons in the DOM
   */
  findCartIcons() {
    this.cartIcons = [
      // Cart icon bubble
      {
        element: document.querySelector('.cart-count-bubble'),
        countElement: document.querySelector('.cart-count-bubble .cart-count'),
        loadingElement: document.querySelector('.cart-count-bubble + .cart-loading'),
        type: 'bubble'
      },
      // Header cart icon (olevorr style)
      {
        element: document.querySelector('.olevorr-header__cart-count'),
        countElement: document.querySelector('.olevorr-header__cart-count'),
        loadingElement: document.querySelector('.olevorr-header__cart-loading'),
        type: 'header'
      },
      // Unified header cart icon
      {
        element: document.querySelector('#cart-count'),
        countElement: document.querySelector('#cart-count'),
        loadingElement: document.querySelector('.cart-loading-indicator'),
        type: 'unified'
      },
      // Mobile navigation cart icon
      {
        element: document.querySelector('.mobile-nav__badge[data-cart-count]'),
        countElement: document.querySelector('.mobile-nav__badge[data-cart-count]'),
        loadingElement: document.querySelector('.mobile-nav__loading[data-cart-loading]'),
        type: 'mobile'
      }
    ].filter(icon => icon.element); // Only keep icons that exist in DOM
    
    console.log('Cart Icon Manager: Found', this.cartIcons.length, 'cart icons');
  }

  /**
   * Bind event listeners
   */
  bindEvents() {
    // Listen for cart manager events
    document.addEventListener('cart:updated', (event) => {
      const count = event.detail.cartData?.item_count || 0;
      this.updateAllIcons(count);
    });

    document.addEventListener('cart:loading', (event) => {
      this.showLoading();
    });

    document.addEventListener('cart:error', (event) => {
      this.hideLoading();
      console.warn('Cart operation failed:', event.detail.message);
    });

    // Listen for cart count updates
    document.addEventListener('cart:count-updated', (event) => {
      this.updateAllIcons(event.detail.count);
    });

    // Handle page visibility changes to sync cart state
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden && window.cartManager) {
        window.cartManager.syncCartState();
      }
    });
  }

  /**
   * Update all cart icons with new count
   * @param {number} count - New cart item count
   */
  updateAllIcons(count) {
    console.log('Cart Icon Manager: Updating all icons with count:', count);
    this.cartIcons.forEach(icon => {
      this.updateIcon(icon, count);
    });

    // Also update mobile navigation directly if available
    if (window.mobileNavigation && typeof window.mobileNavigation.updateCartBadge === 'function') {
      console.log('Cart Icon Manager: Updating mobile navigation directly');
      window.mobileNavigation.updateCartBadge(count);
    }
  }

  /**
   * Update individual cart icon
   * @param {Object} icon - Icon configuration object
   * @param {number} count - New cart item count
   */
  updateIcon(icon, count) {
    if (!icon.element) return;

    const displayCount = count > 99 ? '99+' : count.toString();
    
    // Update count text
    if (icon.countElement) {
      icon.countElement.textContent = displayCount;
    }

    // Handle visibility and accessibility
    if (count === 0) {
      this.hideIcon(icon);
    } else {
      this.showIcon(icon);
    }

    // Update data attributes
    if (icon.element.hasAttribute('data-cart-count')) {
      icon.element.setAttribute('data-cart-count', count);
    }

    // Trigger animation for visual feedback
    this.animateUpdate(icon);
  }

  /**
   * Show cart icon
   * @param {Object} icon - Icon configuration object
   */
  showIcon(icon) {
    if (icon.element) {
      icon.element.style.display = '';
      icon.element.classList.remove('hidden');
      icon.element.setAttribute('aria-hidden', 'false');
      
      // Special handling for different icon types
      if (icon.type === 'bubble' && icon.element.parentElement) {
        icon.element.parentElement.setAttribute('aria-hidden', 'false');
      }
    }
  }

  /**
   * Hide cart icon
   * @param {Object} icon - Icon configuration object
   */
  hideIcon(icon) {
    if (icon.element) {
      if (icon.type === 'bubble') {
        // For bubble, hide the entire bubble
        icon.element.style.display = 'none';
        icon.element.classList.add('hidden');
      } else {
        // For other icons, just hide the count
        icon.element.style.display = 'none';
      }
      
      icon.element.setAttribute('aria-hidden', 'true');
      
      // Special handling for different icon types
      if (icon.type === 'bubble' && icon.element.parentElement) {
        icon.element.parentElement.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Show loading state for all icons
   */
  showLoading() {
    this.cartIcons.forEach(icon => {
      if (icon.loadingElement) {
        icon.loadingElement.classList.remove('hidden');
        icon.loadingElement.setAttribute('aria-hidden', 'false');
      }
    });
  }

  /**
   * Hide loading state for all icons
   */
  hideLoading() {
    this.cartIcons.forEach(icon => {
      if (icon.loadingElement) {
        icon.loadingElement.classList.add('hidden');
        icon.loadingElement.setAttribute('aria-hidden', 'true');
      }
    });
  }

  /**
   * Animate icon update for visual feedback
   * @param {Object} icon - Icon configuration object
   */
  animateUpdate(icon) {
    if (!icon.element) return;

    // Add update animation class
    icon.element.classList.add('cart-icon-updating');
    
    // Remove animation class after animation completes
    setTimeout(() => {
      if (icon.element) {
        icon.element.classList.remove('cart-icon-updating');
      }
    }, 300);
  }

  /**
   * Refresh cart icons (useful after DOM changes)
   */
  refresh() {
    this.findCartIcons();
    if (window.cartManager) {
      this.updateAllIcons(window.cartManager.getItemCount());
    }
  }

  /**
   * Get current cart count from any visible icon
   * @returns {number} Current cart count
   */
  getCurrentCount() {
    for (const icon of this.cartIcons) {
      if (icon.element && icon.element.hasAttribute('data-cart-count')) {
        return parseInt(icon.element.getAttribute('data-cart-count')) || 0;
      }
    }
    return 0;
  }

  /**
   * Manually set cart count (for testing or external updates)
   * @param {number} count - Cart item count
   */
  setCount(count) {
    this.updateAllIcons(count);
  }
}

// CSS for cart icon animations
const cartIconStyles = `
  .cart-icon-updating {
    animation: cart-icon-pulse 0.3s ease-in-out;
  }
  
  @keyframes cart-icon-pulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.1); }
    100% { transform: scale(1); }
  }
  
  /* Smooth transitions for cart count changes */
  .cart-count-bubble,
  .olevorr-header__cart-count,
  #cart-count,
  .mobile-nav__badge {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  
  /* Loading state styles */
  .cart-loading,
  .olevorr-header__cart-loading,
  .cart-loading-indicator,
  .mobile-nav__loading {
    opacity: 0;
    transition: opacity 0.2s ease;
  }
  
  .cart-loading:not(.hidden),
  .olevorr-header__cart-loading:not(.hidden),
  .cart-loading-indicator:not(.hidden),
  .mobile-nav__loading:not(.hidden) {
    opacity: 1;
  }
`;

// Inject styles
if (!document.querySelector('#cart-icon-styles')) {
  const styleSheet = document.createElement('style');
  styleSheet.id = 'cart-icon-styles';
  styleSheet.textContent = cartIconStyles;
  document.head.appendChild(styleSheet);
}

// Initialize cart icon manager
window.cartIconManager = new CartIconManager();

// Export for module systems
if (typeof module !== 'undefined' && module.exports) {
  module.exports = CartIconManager;
}